import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useApi from 'vl-common/src/hooks/useApi';
import { Button, Modal } from 'antd';
import { ChooseProvider } from 'vl-common/src/components/TimeLine/ChooseProvider';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';

interface Props {
  visible: boolean;
  patient: any;
  investigation: any;
  onDone: () => void;
  onClose: () => void;
  onErrorMessage: (errorMessage: string) => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

const DEFAULT_PROVIDERS_PAGE_SIZE = 10;

function ChooseProviderModal({ visible, patient, investigation, onDone, onClose, onErrorMessage }: Props) {
  const { t } = useTranslation('common');
  const { Api, toasted, consumed } = useApi();
  const [postcode, setPostcode] = useState(patient.postcode);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { service_type_code, appt_id } = investigation;

  const {
    data: paginatedProvidersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: providersLoading
  } = useInfiniteQuery({
    queryKey: ['providers', service_type_code, postcode, appt_id, patient.filter_tags],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      try {
        const response = await Api.providers.getServiceImagingProviders({
          type: service_type_code,
          geo_postcode: postcode,
          appt_id,
          tag_filter: true,
          page: pageParam
        });
        return response.data?.imgprovs ?? [];
      } catch (err: unknown) {
        const e = err as ApiError;
        if (e.response?.status) {
          consumed(e);
          setErrorMessage(e.response?.data?.message || 'An unknown error occurred, please try again.');
        }
        return [];
      }
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === DEFAULT_PROVIDERS_PAGE_SIZE ? allPages.length + 1 : undefined,
    enabled: !!visible && !!postcode
  });

  const { mutate: updateProvider } = useMutation({
    mutationKey: ['updateProvider'],
    mutationFn: async (provider: any) => {
      const ireq_id = investigation.ireq_id || investigation.id;
      const response = await Api.clinicians.updateInvestigationProvider(
        investigation.appt_id,
        provider.hospital_id,
        investigation.service_type_code,
        ireq_id
      );
      return response;
    },
    onSuccess: () => {
      onDone();
    },
    onError: (error: any) => {
      const { response } = error;

      if (response?.data?.message) {
        onErrorMessage(response.data.message);
      } else {
        toasted(error.message);
      }
      onClose();
    }
  });

  const onProviderSelected = (provider: any) => {
    updateProvider(provider);
  };

  const onPostcodeSearch = (value?: string) => {
    setErrorMessage(null);
    setPostcode(value || '');
  };

  const onLoadMore = () => {
    if (isFetchingNextPage) return;
    fetchNextPage();
  };

  const providers = paginatedProvidersData?.pages.flat() ?? [];

  return (
    <Modal
      title="Choose Provider"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} data-testid="cancel-button">
          {t('Cancel')}
        </Button>
      ]}
    >
      <ChooseProvider
        providers={providers}
        initialPostcode={postcode}
        onSearch={onPostcodeSearch}
        errorMessage={errorMessage}
        isDataLoading={providersLoading || isFetchingNextPage}
        onProviderSelected={onProviderSelected}
        onLoadMore={onLoadMore}
        hasMoreData={hasNextPage}
      />
    </Modal>
  );
}

export { ChooseProviderModal };
